import { useRef, useState, type ChangeEvent, type FormEvent, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'

import { ApiError, useAuth } from '@forbiz/web-kit'
import type {
  CompanyDetailResponse,
  OrgMember,
  PointTransactionItem,
} from '@forbiz/contracts'

import { myCompanyApi } from '../lib/myCompany.js'
import { useAsync } from '../lib/useAsync.js'
import styles from '../app.module.css'
import m from './MyCompanyPage.module.css'

/**
 * 기업 관리자 '내 회사' 화면 (BIZ-64) — 토스식 카드 리디자인.
 *  1) 회사 정보 — 계약 조건은 조회만(운영자 소관), 연락 정보(대표자·주소·알림번호·청구담당자)만 편집.
 *  2) 구성원 — 목록 + 초대(링크 수동 전달) + 활성/비활성.
 *  3) 추천(바로가기 — 본문은 /referral) · 4) 포인트(읽기 전용).
 * 기업 관리자만. 서버가 세션 companyId로 스코프하므로 남의 회사는 접근 자체가 안 된다.
 */

const CONTRACT_STATUS: Record<string, string> = {
  ACTIVE: '계약 중',
  SUSPENDED: '일시 중지',
  TERMINATED: '종료',
}
const MEMBER_STATUS: Record<string, string> = {
  ACTIVE: '활성',
  INVITED: '초대 중',
  SUSPENDED: '정지',
  DEACTIVATED: '비활성',
}
const ROLE_LABEL: Record<string, string> = {
  COMPANY_ADMIN: '관리자',
  COMPANY_MEMBER: '담당자',
}

function Kv({ label, value }: { label: string; value: string }) {
  return (
    <div className={m.kvRow}>
      <span className={m.kvLabel}>{label}</span>
      <span className={m.kvValue}>{value}</span>
    </div>
  )
}

/** 법인 사업자 판별(BIZ-116) — 서버 isCorporateBizNo와 동일. 사업자번호 가운데 2자리 81~88. */
function isCorporateBizNo(bizRegNo: string | null): boolean {
  if (!bizRegNo) return false
  const d = bizRegNo.replace(/\D/g, '')
  if (d.length !== 10) return false
  const mid = Number(d.slice(3, 5))
  return mid >= 81 && mid <= 88
}

// ── 결제 방식(선불/후불) ─────────────────────────────────────
// 후불 개방 = 신청(REQUESTED) 후 운영자 승인(ACTIVE), BIZ-149. 여기선 상태 표시 + 법인 사업자의 셀프 신청(BIZ-116).
function PaymentMethodSection({
  company,
  onChanged,
}: {
  company: CompanyDetailResponse['company']
  onChanged: () => void
}) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [done, setDone] = useState(false)
  const [uploading, setUploading] = useState(false)
  const docRef = useRef<HTMLInputElement>(null)

  async function onPickDoc(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    setUploading(true)
    setError('')
    try {
      await myCompanyApi.uploadPostpaidDoc(file)
      onChanged()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '사업자등록증을 올리지 못했어요')
    } finally {
      setUploading(false)
    }
  }

  // 결제 방식은 후불 자격(postpaidStatus, BIZ-149)만 본다 — 가입 승인과 무관.
  const status = company.postpaidStatus
  const eligible = isCorporateBizNo(company.bizRegNo)

  async function apply() {
    setBusy(true)
    setError('')
    try {
      await myCompanyApi.requestPostpaid()
      setDone(true)
      onChanged()
    } catch (e) {
      setError(e instanceof ApiError ? e.message : '신청하지 못했어요')
    } finally {
      setBusy(false)
    }
  }

  let pill: string
  let pillCls: string | undefined
  let body: string
  let action: ReactNode = null
  const applyButton = (label: string) => (
    <button data-variant="primary" onClick={() => void apply()} disabled={busy || done}>
      {busy ? '신청 중…' : done ? '신청 완료' : label}
    </button>
  )
  if (status === 'ACTIVE') {
    pill = '후불 이용 중'
    pillCls = m.pillActive
    body = '매월 청구서로 결제하는 후불(월청구)을 이용 중이에요.'
  } else if (status === 'SUSPENDED') {
    pill = '후불 정지'
    pillCls = m.pillWarn
    body = '미납으로 후불이 정지되어 현재 선불로 주문해요. 밀린 청구를 완납하면 후불이 자동으로 다시 열려요.'
  } else if (status === 'REQUESTED') {
    pill = '신청 검토 중'
    pillCls = m.pillWarn
    body = '후불 전환을 신청했어요. 운영자 확인 후 열리면 안내해 드릴게요. 그때까지는 선불로 주문해요.'
  } else if (status === 'REJECTED') {
    pill = '신청 반려'
    pillCls = m.pillMuted
    body = eligible
      ? '후불 전환 신청이 반려됐어요. 지금은 선불로 주문해요. 사업자등록증 등 정보를 보완해 다시 신청할 수 있어요.'
      : '후불 전환 신청이 반려됐어요. 지금은 선불로 주문해요.'
    if (eligible) action = applyButton('다시 신청')
  } else {
    pill = '선불'
    pillCls = m.pillMuted
    if (eligible) {
      body = '지금은 주문할 때 바로 결제하는 선불이에요. 법인 사업자라면 후불(월청구)로 전환을 신청할 수 있어요. 사업자등록증을 함께 올리면 확인이 빨라져요.'
      action = applyButton('후불 전환 신청')
    } else {
      body = '지금은 주문할 때 바로 결제하는 선불이에요. 후불(월청구)은 법인 사업자만 신청할 수 있어요.'
    }
  }

  return (
    <section className={m.card}>
      <div className={m.cardHead}>
        <span className={m.cardTitle}>결제 방식</span>
        <span className={`${m.pill} ${pillCls}`}>{pill}</span>
        <span className={m.headSpacer} />
        {action}
      </div>
      <p className={m.note}>{body}</p>
      {status !== 'ACTIVE' && eligible ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginTop: 8 }}>
          <input
            ref={docRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,application/pdf"
            onChange={(e) => void onPickDoc(e)}
            style={{ display: 'none' }}
          />
          <span className={m.note} style={{ margin: 0 }}>
            {company.postpaidDocKey ? '사업자등록증 첨부됨 ✓' : '사업자등록증 (선택 · JPG·PNG·PDF)'}
          </span>
          <button
            type="button"
            onClick={() => docRef.current?.click()}
            disabled={uploading}
            style={{
              background: 'none',
              border: 'none',
              padding: 0,
              color: 'var(--color-primary, #00684a)',
              cursor: 'pointer',
              textDecoration: 'underline',
              font: 'inherit',
            }}
          >
            {uploading ? '올리는 중…' : company.postpaidDocKey ? '다시 첨부' : '파일 첨부'}
          </button>
        </div>
      ) : null}
      {error ? <p className={m.note} style={{ color: 'var(--danger, #b4342a)' }}>{error}</p> : null}
    </section>
  )
}

export function MyCompanyPage() {
  const { user } = useAuth()
  const [reloadKey, setReloadKey] = useState(0)
  const { data, loading, error } = useAsync(() => myCompanyApi.get(), [reloadKey])
  const reload = () => setReloadKey((k) => k + 1)

  if (user?.role !== 'COMPANY_ADMIN') {
    return (
      <div className={m.page}>
        <div className={m.pageHead}>
          <h1 className={m.pageTitle}>내 회사</h1>
        </div>
        <div className={m.card}>
          <p className={styles.muted}>기업 관리자만 볼 수 있는 화면입니다.</p>
        </div>
      </div>
    )
  }

  return (
    <div className={m.page}>
      <div className={m.pageHead}>
        <h1 className={m.pageTitle}>내 회사</h1>
        <p className={m.pageSub}>회사 연락 정보와 구성원을 관리해요. 계약 조건 변경은 운영팀에 문의하세요.</p>
      </div>

      {error ? (
        <div className={styles.error}>{error}</div>
      ) : loading ? (
        <div className={m.card}>
          <p className={styles.muted}>불러오는 중…</p>
        </div>
      ) : data ? (
        <>
          <CompanyInfoSection detail={data} onSaved={reload} />
          <PaymentMethodSection company={data.company} onChanged={reload} />
          <PointsSection />
          <ReferralLinkCard />
          <MembersSection detail={data} selfId={user.id} onChanged={reload} />
        </>
      ) : null}
    </div>
  )
}

// ── 회사 정보 ───────────────────────────────────────────────
function CompanyInfoSection({
  detail,
  onSaved,
}: {
  detail: CompanyDetailResponse
  onSaved: () => void
}) {
  const { company, billingContact } = detail
  const [editing, setEditing] = useState(false)

  const [ceoName, setCeoName] = useState(company.ceoName ?? '')
  const [address, setAddress] = useState(company.address ?? '')
  const [notifyPhone, setNotifyPhone] = useState(company.notifyPhone ?? '')
  const [bcName, setBcName] = useState(billingContact?.name ?? '')
  const [bcEmail, setBcEmail] = useState(billingContact?.email ?? '')
  const [bcPhone, setBcPhone] = useState(billingContact?.phone ?? '')
  const [busy, setBusy] = useState(false)
  const [formError, setFormError] = useState('')

  function open() {
    setCeoName(company.ceoName ?? '')
    setAddress(company.address ?? '')
    setNotifyPhone(company.notifyPhone ?? '')
    setBcName(billingContact?.name ?? '')
    setBcEmail(billingContact?.email ?? '')
    setBcPhone(billingContact?.phone ?? '')
    setFormError('')
    setEditing(true)
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    setBusy(true)
    setFormError('')
    try {
      const hasBc = bcName.trim() !== '' && bcEmail.trim() !== ''
      await myCompanyApi.update({
        ceoName: ceoName.trim() || undefined,
        address: address.trim() || undefined,
        notifyPhone: notifyPhone.trim() || undefined,
        billingContact: hasBc
          ? { name: bcName.trim(), email: bcEmail.trim(), phone: bcPhone.trim() || undefined }
          : null,
      })
      setEditing(false)
      onSaved()
    } catch (err) {
      setFormError(err instanceof ApiError ? err.message : '저장하지 못했습니다')
    } finally {
      setBusy(false)
    }
  }

  const contractLabel = CONTRACT_STATUS[company.contractStatus] ?? company.contractStatus
  const contractPill = company.contractStatus === 'ACTIVE' ? m.pillActive : m.pillMuted

  return (
    <section className={m.card}>
      <div className={m.cardHead}>
        <span className={m.companyName}>{company.name}</span>
        <span className={`${m.pill} ${contractPill}`}>{contractLabel}</span>
        <span className={m.headSpacer} />
        {!editing ? (
          <button data-variant="secondary" onClick={open}>
            연락 정보 수정
          </button>
        ) : null}
      </div>

      {editing ? (
        <form className={styles.settingEditor} onSubmit={onSubmit}>
          <div className={styles.editorTitle}>연락 정보 수정</div>
          {formError ? <div className={styles.error}>{formError}</div> : null}
          <label className={styles.field}>
            <span className={styles.label}>대표자</span>
            <input value={ceoName} onChange={(e) => setCeoName(e.target.value)} maxLength={50} disabled={busy} />
          </label>
          <label className={styles.field}>
            <span className={styles.label}>주소</span>
            <input value={address} onChange={(e) => setAddress(e.target.value)} maxLength={200} disabled={busy} />
          </label>
          <label className={styles.field}>
            <span className={styles.label}>알림 수신 번호 (휴대폰)</span>
            <input
              value={notifyPhone}
              onChange={(e) => setNotifyPhone(e.target.value)}
              placeholder="01012345678"
              disabled={busy}
            />
          </label>
          <div className={styles.editorTitle}>청구 담당자 (비우면 삭제)</div>
          <label className={styles.field}>
            <span className={styles.label}>이름</span>
            <input value={bcName} onChange={(e) => setBcName(e.target.value)} maxLength={50} disabled={busy} />
          </label>
          <label className={styles.field}>
            <span className={styles.label}>이메일</span>
            <input value={bcEmail} onChange={(e) => setBcEmail(e.target.value)} disabled={busy} />
          </label>
          <label className={styles.field}>
            <span className={styles.label}>전화 (선택)</span>
            <input value={bcPhone} onChange={(e) => setBcPhone(e.target.value)} disabled={busy} />
          </label>
          <div className={styles.actions}>
            <button type="button" data-variant="secondary" onClick={() => setEditing(false)} disabled={busy}>
              취소
            </button>
            <button type="submit" data-variant="primary" disabled={busy}>
              {busy ? '저장 중…' : '저장'}
            </button>
          </div>
        </form>
      ) : (
        <div className={m.kv}>
          <Kv label="사업자번호" value={company.bizRegNo ?? '—'} />
          <Kv label="대표자" value={company.ceoName ?? '—'} />
          <Kv label="주소" value={company.address ?? '—'} />
          <Kv label="계약 기간" value={`${company.contractStartAt} ~ ${company.contractEndAt ?? '무기한'}`} />
          <Kv label="청구 조건" value={`청구서 발행 후 ${company.paymentDueDays}일 내 지급`} />
          <Kv label="알림 번호" value={company.notifyPhone ?? '—'} />
          <Kv
            label="청구 담당자"
            value={
              billingContact
                ? `${billingContact.name} (${billingContact.email}${billingContact.phone ? `, ${billingContact.phone}` : ''})`
                : '—'
            }
          />
        </div>
      )}
    </section>
  )
}

// ── 포인트 (읽기 전용) ───────────────────────────────────────
const POINT_TYPE_LABEL: Record<PointTransactionItem['type'], string> = {
  EARN: '적립',
  REDEEM: '사용',
  CLAWBACK: '회수',
  EXPIRE: '만료',
  ADJUST: '조정',
  REFUND: '환급',
}

function signedPoint(n: number): string {
  const s = n.toLocaleString('ko-KR')
  return n > 0 ? `+${s}` : s
}

/**
 * 내 회사 포인트 잔액·내역 (BIZ-74). 기업 관리자는 조회만 — 적립은 주문 시, 조정은 운영자 소관.
 */
function PointsSection() {
  const { data, loading, error } = useAsync(() => myCompanyApi.points(), [])
  const txs = data?.transactions ?? []
  const today = new Date().toISOString().slice(0, 10)
  const dot = (d: string) => d.slice(5).replace('-', '.') // 'YYYY-MM-DD' → 'MM.DD'

  return (
    <section className={m.card}>
      <div className={m.cardHead}>
        <span className={m.cardTitle}>포인트</span>
      </div>

      {error ? (
        <div className={styles.error}>{error}</div>
      ) : loading ? (
        <p className={styles.muted}>불러오는 중…</p>
      ) : (
        <>
          <div className={m.balanceHero}>
            <div className={m.balanceLabel}>사용 가능 포인트</div>
            <div className={m.balanceNum}>{(data?.balances.available ?? 0).toLocaleString('ko-KR')}P</div>
            {data && (data.balances.pending > 0 || data.balances.expiringSoon > 0) ? (
              <div className={m.balanceSub}>
                {data.balances.pending > 0 ? <span>대기중 {data.balances.pending.toLocaleString('ko-KR')}P · 다음 달 지급 예정</span> : null}
                {data.balances.expiringSoon > 0 ? (
                  <span>30일 내 소멸 {data.balances.expiringSoon.toLocaleString('ko-KR')}P</span>
                ) : null}
              </div>
            ) : null}
          </div>
          {txs.length === 0 ? (
            <p className={styles.muted}>아직 포인트 내역이 없어요. 주문할 때 ‘적립’을 고르면 쌓여요.</p>
          ) : (
            <div className={m.txList}>
              {txs.map((t) => (
                <div key={t.id} className={m.txRow}>
                  <div className={m.txMeta}>
                    <div className={m.txTop}>
                      {POINT_TYPE_LABEL[t.type] ?? t.type}
                      <span className={m.txDate}>{t.createdAt.slice(0, 10)}</span>
                    </div>
                    {t.memo ? <div className={m.txMemo}>{t.memo}</div> : null}
                    {t.lotRemaining != null && t.availableAt ? (
                      <div className={m.txMemo}>
                        {t.availableAt > today
                          ? `${dot(t.availableAt)}부터 사용 가능`
                          : t.expiresAt
                            ? `${dot(t.expiresAt)}까지`
                            : ''}
                      </div>
                    ) : null}
                  </div>
                  <div className={m.txRight}>
                    <div className={`${m.txAmount} ${t.amount < 0 ? m.txAmountNeg : ''}`}>{signedPoint(t.amount)}P</div>
                    <div className={m.txBalance}>잔액 {t.balanceAfter.toLocaleString('ko-KR')}P</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </section>
  )
}

// ── 추천 ────────────────────────────────────────────────────
/**
 * 추천은 독립 페이지(/referral, 회의 S5)로 옮겼다 — 여기선 바로가기만 둔다.
 */
function ReferralLinkCard() {
  const navigate = useNavigate()
  return (
    <button type="button" className={`${m.card} ${m.linkCard}`} onClick={() => navigate('/referral')}>
      <span className={m.linkCardBody}>
        <span className={m.cardTitle}>추천</span>
        <span className={m.linkCardSub}>지인에게 포비즈를 추천하고 포인트를 받아요</span>
      </span>
      <span className={m.linkCardGo} aria-hidden="true">
        →
      </span>
    </button>
  )
}

// ── 구성원 ──────────────────────────────────────────────────
function MembersSection({
  detail,
  selfId,
  onChanged,
}: {
  detail: CompanyDetailResponse
  selfId: number
  onChanged: () => void
}) {
  const [inviteOpen, setInviteOpen] = useState(false)
  const [openId, setOpenId] = useState<number | null>(null)
  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const [phone, setPhone] = useState('')
  const [role, setRole] = useState<'COMPANY_ADMIN' | 'COMPANY_MEMBER'>('COMPANY_MEMBER')
  const [inviteUrl, setInviteUrl] = useState('')
  const [busy, setBusy] = useState(false)
  const [formError, setFormError] = useState('')
  const [rowError, setRowError] = useState('')

  function openInvite() {
    setInviteOpen(true)
    setEmail('')
    setName('')
    setPhone('')
    setRole('COMPANY_MEMBER')
    setInviteUrl('')
    setFormError('')
  }
  function closeInvite() {
    setInviteOpen(false)
    if (inviteUrl) onChanged()
  }

  async function onInvite(e: FormEvent) {
    e.preventDefault()
    setBusy(true)
    setFormError('')
    try {
      const res = await myCompanyApi.inviteMember({
        email: email.trim(),
        name: name.trim(),
        role,
        ...(phone.trim() ? { phone: phone.trim() } : {}),
      })
      setInviteUrl(res.inviteUrl.startsWith('http') ? res.inviteUrl : window.location.origin + res.inviteUrl)
    } catch (err) {
      setFormError(err instanceof ApiError ? err.message : '초대하지 못했습니다')
    } finally {
      setBusy(false)
    }
  }

  async function toggle(mem: OrgMember) {
    const next = mem.status === 'ACTIVE' ? 'DEACTIVATED' : 'ACTIVE'
    const verb = next === 'ACTIVE' ? '활성화' : '비활성화'
    if (!window.confirm(`'${mem.name}' 님을 ${verb}할까요?`)) return
    setRowError('')
    try {
      await myCompanyApi.setMemberStatus(mem.id, { status: next })
      onChanged()
    } catch (err) {
      setRowError(err instanceof ApiError ? err.message : `${verb}하지 못했습니다`)
    }
  }

  async function changeRole(mem: OrgMember, newRole: 'COMPANY_ADMIN' | 'COMPANY_MEMBER') {
    if (newRole === mem.role) return
    setRowError('')
    try {
      await myCompanyApi.setMemberRole(mem.id, { role: newRole })
      onChanged()
    } catch (err) {
      setRowError(err instanceof ApiError ? err.message : '역할을 변경하지 못했습니다')
    }
  }

  async function remove(mem: OrgMember) {
    if (!window.confirm(`'${mem.name}' 님을 삭제할까요? 되돌릴 수 없습니다.`)) return
    setRowError('')
    try {
      await myCompanyApi.removeMember(mem.id)
      onChanged()
    } catch (err) {
      setRowError(err instanceof ApiError ? err.message : '삭제하지 못했습니다')
    }
  }

  return (
    <section className={m.card}>
      <div className={m.cardHead}>
        <span className={m.cardTitle}>구성원</span>
        <span className={m.headSpacer} />
        <button data-variant="secondary" onClick={openInvite} disabled={inviteOpen}>
          ＋ 담당자 초대
        </button>
      </div>

      {inviteOpen ? (
        <div className={styles.settingEditor} style={{ marginBottom: 'var(--space-md)' }}>
          {inviteUrl ? (
            <>
              <div className={styles.editorTitle}>초대 링크가 생성되었어요</div>
              <p className={m.note}>아래 링크를 담당자에게 전달하세요. (이메일 자동 발송은 준비 중)</p>
              <div className={m.linkBox}>{inviteUrl}</div>
              <div className={styles.actions}>
                <button type="button" data-variant="primary" onClick={closeInvite}>
                  완료
                </button>
              </div>
            </>
          ) : (
            <form onSubmit={onInvite}>
              <div className={styles.editorTitle}>담당자 초대</div>
              {formError ? <div className={styles.error}>{formError}</div> : null}
              <label className={styles.field}>
                <span className={styles.label}>이름</span>
                <input value={name} onChange={(e) => setName(e.target.value)} maxLength={50} disabled={busy} />
              </label>
              <label className={styles.field}>
                <span className={styles.label}>이메일</span>
                <input value={email} onChange={(e) => setEmail(e.target.value)} disabled={busy} />
              </label>
              <label className={styles.field}>
                <span className={styles.label}>휴대폰 (선택 · 입력 시 알림톡으로 초대 발송)</span>
                <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="01012345678" disabled={busy} />
              </label>
              <label className={styles.field}>
                <span className={styles.label}>역할</span>
                <select value={role} onChange={(e) => setRole(e.target.value as typeof role)} disabled={busy}>
                  <option value="COMPANY_MEMBER">담당자</option>
                  <option value="COMPANY_ADMIN">관리자</option>
                </select>
              </label>
              <div className={styles.actions}>
                <button type="button" data-variant="secondary" onClick={closeInvite} disabled={busy}>
                  취소
                </button>
                <button type="submit" data-variant="primary" disabled={busy || name.trim() === '' || email.trim() === ''}>
                  {busy ? '초대 중…' : '초대'}
                </button>
              </div>
            </form>
          )}
        </div>
      ) : null}

      {rowError ? <div className={styles.error}>{rowError}</div> : null}

      <div className={m.memberList}>
        {detail.members.map((mem) => {
          const statusPill =
            mem.status === 'ACTIVE' ? m.pillActive : mem.status === 'INVITED' ? m.pillWarn : m.pillMuted
          const isSelf = mem.id === selfId
          const isOpen = openId === mem.id
          return (
            <div key={mem.id} className={m.memberRow}>
              <div className={m.memberMain}>
                <span className={m.avatar}>{mem.name.trim().charAt(0) || '?'}</span>
                <div className={m.memberMeta}>
                  <div className={m.memberName}>
                    <span className={m.memberNameText}>{mem.name}</span>
                    {mem.role === 'COMPANY_ADMIN' ? (
                      <span className={`${m.pill} ${m.pillAdmin}`}>{ROLE_LABEL[mem.role]}</span>
                    ) : null}
                    <span className={`${m.pill} ${statusPill}`}>{MEMBER_STATUS[mem.status] ?? mem.status}</span>
                  </div>
                  <div className={m.memberEmail}>{mem.email}</div>
                </div>
                {isSelf ? (
                  <span className={m.selfTag}>나</span>
                ) : (
                  <button
                    type="button"
                    className={m.manageBtn}
                    aria-expanded={isOpen}
                    onClick={() => setOpenId(isOpen ? null : mem.id)}
                  >
                    {isOpen ? '닫기' : '관리'}
                  </button>
                )}
              </div>
              {!isSelf && isOpen ? (
                <div className={m.memberActions}>
                  <label className={m.actionRole}>
                    <span>역할</span>
                    <select
                      value={mem.role}
                      aria-label="역할"
                      onChange={(e) => void changeRole(mem, e.target.value as 'COMPANY_ADMIN' | 'COMPANY_MEMBER')}
                    >
                      <option value="COMPANY_MEMBER">담당자</option>
                      <option value="COMPANY_ADMIN">관리자</option>
                    </select>
                  </label>
                  <button
                    className={styles.memberToggle}
                    data-variant={mem.status === 'ACTIVE' ? 'danger' : 'secondary'}
                    onClick={() => void toggle(mem)}
                  >
                    {mem.status === 'ACTIVE' ? '비활성화' : '활성화'}
                  </button>
                  <button data-variant="secondary" onClick={() => void remove(mem)}>
                    삭제
                  </button>
                </div>
              ) : null}
            </div>
          )
        })}
      </div>
    </section>
  )
}
